// Perfil do admin logado (`profiles` da própria linha): leitura, edição e
// avatar (bucket `avatars`). Também expõe os helpers de nome usados no
// Cockpit e na sidebar.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface AdminProfile {
  name: string | null;
  avatar_url: string | null;
  phone: string | null;
  bio: string | null;
}

export function useAdminProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["admin-profile", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<AdminProfile> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("name, avatar_url, phone, bio")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return {
        name: data?.name ?? null,
        avatar_url: data?.avatar_url ?? null,
        phone: data?.phone ?? null,
        bio: data?.bio ?? null,
      };
    },
  });
}

function useInvalidateProfile(userId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["admin-profile", userId] });
    qc.invalidateQueries({ queryKey: ["admin-users"] });
  };
}

export function useUpdateAdminProfile() {
  const { user } = useAuth();
  const invalidate = useInvalidateProfile(user?.id);
  return useMutation({
    mutationFn: async (
      patch: Partial<Pick<AdminProfile, "name" | "phone" | "bio" | "avatar_url">>,
    ) => {
      if (!user) throw new Error("Sem sessão");
      const { error } = await supabase.from("profiles").update(patch).eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/** Sobe a imagem para `avatars/<user>/<ts>.<ext>` (upsert) e grava a URL pública no perfil. Devolve a URL. */
export function useUploadAvatar() {
  const { user } = useAuth();
  const invalidate = useInvalidateProfile(user?.id);
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      if (!user) throw new Error("Sem sessão");
      const ext =
        (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: pub.publicUrl })
        .eq("id", user.id);
      if (error) throw error;
      return pub.publicUrl;
    },
    onSuccess: invalidate,
  });
}

// Names auto-derived from the e-mail local part look like "eliveuton.dev",
// so dots/underscores count as separators too.
function nameParts(name: string) {
  return name
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function initialsOf(name: string | null | undefined, fallback = "ES") {
  if (!name) return fallback;
  const parts = nameParts(name);
  if (parts.length === 0) return fallback;
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase() || fallback;
}

export function firstNameOf(name: string | null | undefined, fallback = "Eliveuton") {
  if (!name) return fallback;
  const first = nameParts(name)[0];
  return first ? capitalize(first) : fallback;
}
